// @ts-nocheck
// CameraCapture — câmera IN-APP (estilo Instagram), universal.
// Usa getUserMedia + <video> ao vivo + captura por <canvas> → File JPEG.
// Funciona no WebView do iOS (WKWebView, iOS 14.3+, NSCameraUsageDescription) e
// Android (CAMERA no manifest) e no navegador — sem abrir a câmera nativa do SO.
// Tem botão de galeria como alternativa e fallback se a câmera falhar.
import React, { useEffect, useRef, useState } from 'react';

export const CameraCapture: React.FC<{
  open: boolean;
  onCapture: (file: File) => void;
  onClose: () => void;
  title?: string;
}> = ({ open, onCapture, onClose, title }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [status, setStatus] = useState<'starting' | 'ready' | 'error'>('starting');
  const [busy, setBusy] = useState(false);

  const stop = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
    streamRef.current = null;
  };

  const start = async (mode: 'environment' | 'user') => {
    stop();
    setStatus('starting');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('no-cam');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1920 } },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        v.setAttribute('playsinline', 'true');
        try { await v.play(); } catch (_) {}
      }
      setStatus('ready');
    } catch (e) {
      stop();
      setStatus('error');
    }
  };

  useEffect(() => {
    if (open) { setFacing('environment'); start('environment'); }
    else stop();
    return () => stop();
  }, [open]); // eslint-disable-line

  const flip = () => { const next = facing === 'environment' ? 'user' : 'environment'; setFacing(next); start(next); };

  const shoot = () => {
    const v = videoRef.current;
    if (!v || status !== 'ready' || busy) return;
    const w = v.videoWidth, h = v.videoHeight;
    if (!w || !h) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { setBusy(false); return; }
      ctx.drawImage(v, 0, 0, w, h);
      canvas.toBlob((blob) => {
        setBusy(false);
        if (!blob) return;
        const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
        stop();
        onCapture(file);
      }, 'image/jpeg', 0.92);
    } catch (_) { setBusy(false); }
  };

  const pickGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    stop();
    onCapture(f);
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: '#000', display: 'flex', flexDirection: 'column' }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={pickGallery} style={{ display: 'none' }} />

      {/* preview ao vivo */}
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <video ref={videoRef} playsInline muted autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: facing === 'user' ? 'scaleX(-1)' : 'none', background: '#000' }} />

        {status === 'starting' && <div style={{ position: 'absolute', color: 'rgba(255,255,255,.85)', fontSize: 14 }}>Abrindo câmera…</div>}
        {status === 'error' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center', color: 'rgba(255,255,255,.9)' }}>
            <div style={{ fontSize: 15, maxWidth: 280, lineHeight: 1.5 }}>Não consegui abrir a câmera. Você pode escolher uma foto da galeria.</div>
            <button onClick={() => fileRef.current?.click()} style={{ padding: '12px 22px', borderRadius: 12, border: 'none', background: 'var(--ember)', color: '#1c1208', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Escolher da galeria</button>
          </div>
        )}

        {/* barra superior */}
        <div style={{ position: 'absolute', top: 'calc(14px + env(safe-area-inset-top,0px))', left: 14, right: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2 }}>
          <button onClick={() => { stop(); onClose(); }} aria-label="Fechar" style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.42)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
          {title && <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, opacity: 0.92, textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>{title}</div>}
          <button onClick={flip} aria-label="Virar câmera" style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.42)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M15 3.5h3A2.5 2.5 0 0 1 20.5 6v3" /><path d="M9 20.5H6A2.5 2.5 0 0 1 3.5 18v-3" /><path d="M20.5 9a8.5 8.5 0 0 0-15-3.5" opacity=".55" /><path d="M3.5 15a8.5 8.5 0 0 0 15 3.5" opacity=".55" /></svg>
          </button>
        </div>
      </div>

      {/* controles inferiores */}
      <div style={{ flex: 'none', padding: '18px 28px calc(26px + env(safe-area-inset-bottom,0px))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#000' }}>
        <button onClick={() => fileRef.current?.click()} aria-label="Galeria" style={{ width: 46, height: 46, borderRadius: 10, border: '1px solid rgba(255,255,255,.35)', background: 'rgba(255,255,255,.08)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
        </button>
        <button onClick={shoot} disabled={status !== 'ready' || busy} aria-label="Tirar foto" style={{ width: 74, height: 74, borderRadius: '50%', border: '4px solid #fff', background: busy ? 'rgba(255,255,255,.55)' : '#fff', cursor: status === 'ready' ? 'pointer' : 'default', opacity: status === 'ready' ? 1 : 0.4, boxShadow: '0 0 0 3px rgba(0,0,0,.7) inset' }} />
        <div style={{ width: 46 }} />
      </div>
    </div>
  );
};
